import { Field, useFormikContext } from 'formik';
import { Typography, Grid, Tooltip, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MuiValidTextField from 'src/components/input/MuiValidTextField';
import MuiValidSelect from 'src/components/input/MuiValidSelect';
import CardWrapper from 'src/components/util/CardWrapper';
import MuiCheckbox from 'src/components/input/MuiCheckbox';
import BusinessInfoAddress from 'src/components/BusinessInfoAddress';
import { useContext, useEffect } from 'react';
import BusinessContext from 'src/store/business-context';

const BusinessInfo = ({ natureOfBusOptions = [] }) => {
  const { values, setFieldValue } = useFormikContext();

  const { setSameForMail } = useContext(BusinessContext);

  // Sync dbaName with busName when dbaSameAsPrimary is checked
  useEffect(() => {
    if (values.dbaSameAsPrimary && values.busName !== values.dbaName) {
      setFieldValue('dbaName', values.busName);
    }
  }, [values.dbaSameAsPrimary, values.busName, values.dbaName, setFieldValue]);

  return (
    <>
      <CardWrapper>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <Field
              name="busName"
              component={MuiValidTextField}
              label="Primary Business Name"
              sx={{ width: '100%' }}
              variant="standard"
              inputProps={{ maxLength: 70 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="emailAddr"
              component={MuiValidTextField}
              label="Primary Business Email"
              sx={{ width: '100%' }}
              variant="standard"
              inputProps={{ maxLength: 40 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="natureOfBus"
              component={MuiValidSelect}
              label="Primary Business Category"
              sx={{ width: '100%' }}
              variant="standard"
              itemValues={natureOfBusOptions.map((category) => category.abbv)}
              itemLabels={natureOfBusOptions.map((category) => category.descr)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="ownershipType"
              component={MuiValidSelect}
              label="Ownership Type"
              sx={{ width: '100%' }}
              variant="standard"
              itemValues={['P2', 'C', 'LLC', 'P1', 'O']}
              itemLabels={['Partnership', 'Corporation', 'LLC', 'Sole Proprietorship', 'Other']}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="homebiz"
              component={MuiValidSelect}
              label="Home Business"
              sx={{ width: '100%' }}
              variant="standard"
              itemValues={['Y', 'N']}
              itemLabels={['Yes', 'No']}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="dbaName"
              component={MuiValidTextField}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span>DBA Name</span>
                  <Tooltip title="Doing Business As" arrow placement="right">
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: 'action.active',
                        marginLeft: '4px',
                        marginTop: '-2px',
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </Box>
              }
              sx={{ width: '100%' }}
              variant="standard"
              inputProps={{ maxLength: 70 }}
              InputLabelProps={{ shrink: true }}
              disabled={values.dbaSameAsPrimary}
            />
            <Field
              component={MuiCheckbox}
              name="dbaSameAsPrimary"
              label="Same as Primary"
              onChange={(e) => {
                const isChecked = e.target.checked;
                setFieldValue('dbaSameAsPrimary', isChecked);
                if (isChecked) {
                  setFieldValue('dbaName', values.busName);
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="dateOpenedLoc"
              component={MuiValidTextField}
              label="Date This Location Opened"
              sx={{ width: '100%' }}
              variant="standard"
              type="Date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              name="naicsCode"
              component={MuiValidTextField}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span>NAICS</span>
                  <Tooltip
                    title="North American Industry Classification System"
                    arrow
                    placement="right"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: 'action.active',
                        marginLeft: '4px',
                        marginTop: '-2px',
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                </Box>
              }
              sx={{ width: '100%', marginBottom: 2 }}
              variant="standard"
              inputProps={{ maxLength: 12 }}
              InputLabelProps={{ shrink: true }}
            />

            <Typography>
              <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                New for 2023!
              </Typography>{' '}
              As per new legislation, you must file your NAICS classification number with your
              filing. To look up your code, see this{' '}
              <a href="https://www.naics.com/search/" target="_blank" rel="noreferrer">
                link
              </a>
              .
            </Typography>
          </Grid>
        </Grid>

        {/*Beginning Of Business Location*/}
        <BusinessInfoAddress
          label="Business Location"
          cityFieldName="busCity"
          zipFieldName="busZip"
          streetFieldName="busLoc"
        />

        <Field
          component={MuiCheckbox}
          name="sameForMail"
          label={'Check if above information is the same for the mailing address'}
          onChange={(e) => {
            setSameForMail(e.target.checked);
            setFieldValue('sameForMail', e.target.checked);
          }}
        />

        {values.sameForMail === false && (
          <BusinessInfoAddress
            label="Mailing Address"
            cityFieldName="mailCity"
            zipFieldName="mailZip"
            streetFieldName="mailAddr"
          />
        )}
      </CardWrapper>
    </>
  );
};

export default BusinessInfo;
